import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const isCancel = location.pathname.includes("cancel");
  const [status, setStatus] = useState(isCancel ? "cancelled" : "checking");
  const [attempts, setAttempts] = useState(0);

  const sessionId = new URLSearchParams(location.search).get("session_id");

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let active = true;
    const poll = async (n) => {
      if (!active) return;
      if (n > 8) { setStatus("timeout"); return; }
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await refresh();
          setStatus("success");
          return;
        }
      } catch (e) {}
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { active = false; };
  }, [sessionId, isCancel, refresh]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <Logo size="lg" className="justify-center mb-8" />
        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="h-10 w-10 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Confirming your payment…</p>
          </div>
        )}
        {status === "success" && (
          <div data-testid="payment-success">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-slate-900">You're all set!</h1>
            <p className="mt-2 text-slate-600">Your Syllabify account is unlocked. Enjoy lifetime access.</p>
            <Button onClick={() => navigate("/dashboard")} data-testid="goto-dashboard-btn" className="mt-6 bg-blue-600 hover:bg-blue-700 rounded-lg px-6 py-5">Go to dashboard</Button>
          </div>
        )}
        {(status === "cancelled" || status === "timeout") && (
          <div data-testid="payment-cancelled">
            <XCircle className="h-14 w-14 text-slate-400 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-slate-900">{status === "timeout" ? "Still processing" : "Payment cancelled"}</h1>
            <p className="mt-2 text-slate-600">{status === "timeout" ? "Your payment is taking a moment. Check your dashboard shortly." : "No charge was made. You can unlock Syllabify anytime."}</p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button onClick={() => navigate("/upgrade")} variant="outline" className="rounded-lg">Try again</Button>
              <Button onClick={() => navigate("/dashboard")} className="bg-slate-900 hover:bg-slate-800 rounded-lg">Dashboard</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
